import "../styles/job.css";
import { lazy, Suspense } from "react";

import DataAnalyticsHero from "./DataAnalyticsHero";
import DataAnalyticsJobRoles from "./DataAnalyticsJobRoles";

/* 🔥 Lazy-load heavy sections to avoid blocking navigation */
const Demand = lazy(() => import("../pages/DataAnalyticsDemand.js"));
const FeaturesSection = lazy(() => import("../pages/FeaturesSection"));
const ScrollingFeedback = lazy(() => import("../pages/ScrollingFeedback"));

export default function DataAnalyticsOverview() {
  return (
    <>
      {/* ================= HERO BANNER ================= */}
      <DataAnalyticsHero />

      {/* ================= MAIN CONTENT ================= */}
      <section className="genai-page">

        {/* PAGE INTRO */}
        <header className="genai-hero">
          <h1>Data Analytics – Master Core Essentials</h1>
          <p className="subtitle">
            Learn to Analyze Data & Turn Insights into Decisions
          </p>
        </header>

        {/* PROGRAM OVERVIEW */}
        <section className="genai-section">
          <p>
            The Data Analytics Starter Program is designed to introduce learners
            to the world of data analytics in a simple, practical, and hands-on
            manner. This program focuses on building strong foundations in data
            collection, cleaning, analysis, and visualization using widely used
            tools such as Excel, SQL, and basic Python.
          </p>

          <p>
            Learners will work with real-world style datasets to understand how
            data is transformed into meaningful insights. By the end of the
            program, participants will be confident in analyzing data, creating
            visual reports, and presenting insights that support business and
            decision-making needs.
          </p>
        </section>

        {/* JOB ROLES */}
        <DataAnalyticsJobRoles />

        {/* LEARNING OUTCOMES */}
        <section className="genai-section">
          <h2>Key Learning Outcomes</h2>
          <ul className="outcome-list">
            <li>Understand the fundamentals of data analytics with real-life examples</li>
            <li>Work confidently with Excel for data entry, cleaning, and analysis</li>
            <li>Apply formulas and functions to summarize and analyze data</li>
            <li>Write SQL queries to retrieve, filter, and aggregate data</li>
            <li>Use Python & Pandas for basic data handling</li>
            <li>Create charts and visual reports</li>
            <li>Perform end-to-end analysis on real datasets</li>
          </ul>
        </section>

        {/* WHO SHOULD ATTEND */}
        <section className="genai-section">
          <h2>Who Should Attend</h2>
          <ul className="outcome-list">
            <li>Students and fresh graduates</li>
            <li>Beginners with no analytics background</li>
            <li>Professionals from non-technical domains</li>
            <li>Business, HR, finance, marketing professionals</li>
            <li>Anyone interested in data-driven careers</li>
          </ul>

          <p className="salary-note">
            No prior programming or analytics experience is required.
          </p>
        </section>

        {/* CAREER PATHWAYS */}
        <section className="genai-section">
          <h2>Career Pathways, Job Roles & Salary Range in India</h2>

          <div className="salary-table-wrapper">
            <table className="salary-table">
              <thead>
                <tr>
                  <th>Job Role</th>
                  <th>*Entry Salary</th>
                  <th>*Mid-Level Salary</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Data Analyst (Junior)</td><td>₹3 – 6 LPA</td><td>₹6 – 12 LPA</td></tr>
                <tr><td>Business Analyst</td><td>₹4 – 7 LPA</td><td>₹8 – 15 LPA</td></tr>
                <tr><td>Excel / MIS Executive</td><td>₹2.5 – 5 LPA</td><td>₹5 – 9 LPA</td></tr>
                <tr><td>Reporting Analyst</td><td>₹3 – 6 LPA</td><td>₹6 – 11 LPA</td></tr>
                <tr><td>Operations Analyst</td><td>₹3.5 – 6.5 LPA</td><td>₹7 – 13 LPA</td></tr>
                <tr><td>Data Support Executive</td><td>₹3 – 5.5 LPA</td><td>₹6 – 10 LPA</td></tr>
              </tbody>
            </table>

            <p className="salary-note">
              *Salary ranges vary based on skills, projects, experience, and industry demand.
            </p>
          </div>
        </section>

        {/* PROJECT HIGHLIGHT */}
        <section className="genai-section highlight">
          <h2>Project Highlights</h2>
          <p>
            <strong>Capstone Project:</strong> End-to-end data analysis using
            Excel, SQL, and Python with insights presented through dashboards.
          </p>
        </section>

        {/* ================= LAZY-LOADED SECTIONS ================= */}
        <Suspense fallback={<div style={{ padding: 40 }}>Loading insights…</div>}>
          <Demand />
          <FeaturesSection />
          <ScrollingFeedback />
        </Suspense>

      </section>
    </>
  );
}
